import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Col,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row
} from "reactstrap";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value) => {
    if (!value || typeof value !== "string") return false;
    const trimmed = value.trim();
    return trimmed.length > 0 && EMAIL_REGEX.test(trimmed);
};

const OutOfStockEmailModal = ({ isOpen, emailData, onSend, onClose, isSending = false }) => {
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [to, setTo] = useState("");
    const [from, setFrom] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [promoCode, setPromoCode] = useState("");
    const [includePromo, setIncludePromo] = useState(false);
    const [showRecipients, setShowRecipients] = useState(false);
    const [cc, setCc] = useState("");
    const [bcc, setBcc] = useState("");
    const [toError, setToError] = useState("");
    const [fromError, setFromError] = useState("");

    useEffect(() => {
        if (emailData) {
            setTo(emailData?.to || "");
            // From is pre-filled only for Goddiva (from API); other brands leave empty for manual entry
            setFrom(emailData?.from ?? "");
            const normalizedSubject = emailData?.subject ? emailData.subject.replace(/##+/g, "#") : "";
            setSubject(normalizedSubject);
            const defaultMessage = "We're really sorry, it looks like one or more items from your order are currently out of stock.";
            const initialMessage = emailData?.custom_message || emailData?.message || defaultMessage;
            setMessage(initialMessage);
            setPromoCode(emailData?.promo_code || "");
            setIncludePromo(!!emailData?.promo_code);
            setCc(emailData?.cc || "");
            setBcc(emailData?.bcc || "");
            setToError("");
            setFromError("");
            // Reset to confirmation step when modal opens with new data
            setShowEmailForm(false);
        }
    }, [emailData]);

    // Reset form visibility when modal closes
    useEffect(() => {
        if (!isOpen) {
            setShowEmailForm(false);
        }
    }, [isOpen]);

    const items = useMemo(() => {
        if (!emailData?.items) return [];
        if (Array.isArray(emailData.items)) return emailData.items;
        return Object.values(emailData.items);
    }, [emailData]);

    const hasRefundIds = useMemo(() => !!(emailData?.order_refund_ids || []).length, [emailData]);
    const hasItems = useMemo(() => (items || []).length > 0, [items]);

    const previewHtml = useMemo(() => {
        const promoRow = includePromo && promoCode
            ? `<p><strong>Promo code:</strong> ${promoCode}</p>`
            : "";

        const rows = items.map(item => {
            const qty = Number(item.return_quantity || 0);
            const returnAmount = Number(item.return_amount || 0);
            const displayPrice = qty > 1 ? (returnAmount * qty) : returnAmount;
            
            return `
            <tr>
                <td>${item.brand || ""}</td>
                <td>${item.product_title || ""}</td>
                <td>${item.product_sku || ""}</td>
                <td>${qty}</td>
                <td>₹${displayPrice.toFixed(2)}</td>
            </tr>
        `;
        }).join("");

        const totalQty = items.reduce((sum, item) => sum + Number(item.return_quantity || 0), 0);
        const totalAmount = items.reduce((sum, item) => {
            const qty = Number(item.return_quantity || 0);
            const returnAmount = Number(item.return_amount || 0);
            return sum + (qty > 1 ? (returnAmount * qty) : returnAmount);
        }, 0);
        const defaultMessage = "We're really sorry, it looks like one or more items from your order are currently out of stock.";
        const emailMessage = (message && message.trim() 
            ? message 
            : defaultMessage).replace(/\n/g, "<br/>");

        return `
            <div style="font-family: Arial, sans-serif; color: #2c2c2c;">
                <p>Dear ${emailData?.customer_name || ""},</p>
                <p>${emailMessage}</p>
                <p><strong>Order reference:</strong> ${emailData?.order_name ? ("#" + emailData.order_name.replace(/^#+/, "")) : ""}</p>
                <h5 style="margin: 16px 0 8px 0;">Out of Stock item(s):</h5>
                <table style="width:100%;border-collapse:collapse;margin-top:4px;">
                    <thead>
                        <tr>
                            <th style="text-align:left;border-bottom:1px solid #ddd;padding:6px 0;">Brand</th>
                            <th style="text-align:left;border-bottom:1px solid #ddd;padding:6px 0;">Item Name</th>
                            <th style="text-align:left;border-bottom:1px solid #ddd;padding:6px 0;">SKU</th>
                            <th style="text-align:left;border-bottom:1px solid #ddd;padding:6px 0;">Qty</th>
                            <th style="text-align:left;border-bottom:1px solid #ddd;padding:6px 0;">Price</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                    <tfoot>
                        <tr>
                            <td colspan="3" style="text-align:right;padding-top:8px;border-top:2px solid #ddd;font-weight:bold;">Total:</td>
                            <td style="text-align:left;padding-top:8px;border-top:2px solid #ddd;font-weight:bold;">${totalQty}</td>
                            <td style="text-align:left;padding-top:8px;border-top:2px solid #ddd;font-weight:bold;">₹${totalAmount.toFixed(2)}</td>
                        </tr>
                    </tfoot>
                </table>
                ${promoRow}
                <p>Thank you for your understanding and for shopping with us. 💛</p>
            </div>
        `;
    }, [emailData, includePromo, items, message, promoCode]);

    const validateTo = (value) => {
        const v = (value !== undefined ? value : to).trim();
        if (!v) return "Email address is required.";
        if (!EMAIL_REGEX.test(v)) return "Please enter a valid email address.";
        return "";
    };

    const validateFrom = (value) => {
        const v = (value !== undefined ? value : from).trim();
        if (!v) return "From email is required.";
        if (!EMAIL_REGEX.test(v)) return "Please enter a valid email address.";
        return "";
    };

    const handleSend = () => {
        const toErr = validateTo(to);
        const fromErr = validateFrom(from);
        setToError(toErr);
        setFromError(fromErr);
        if (toErr || fromErr) return;
        const trimmedSubject = subject.trim();
        if (!trimmedSubject || (!hasRefundIds && !hasItems)) return;
        if (onSend) {
            // Always use current form values so the email is sent to the updated (edited) address
            onSend({
                order_id: emailData?.order_id || null,
                to: to.trim(),
                from: from.trim() || null,
                subject: trimmedSubject,
                custom_message: message,
                promo_code: includePromo ? promoCode : null,
                cc: cc || null,
                bcc: bcc || null,
                order_refund_ids: emailData?.order_refund_ids || [],
                items: items
            });
        }
    };

    const handleConfirmSendEmail = () => {
        setShowEmailForm(true);
    };

    const handleSkipEmail = () => {
        onClose();
    };

    // Show confirmation step first
    if (!showEmailForm) {
        return (
            <Modal isOpen={isOpen} size="md" toggle={onClose} fade={true}>
                <ModalHeader toggle={onClose}>Out of Stock Notification</ModalHeader>
                <ModalBody>
                    <p style={{ fontWeight: 600, marginBottom: 12, fontSize: '16px' }}>
                        Item(s) are out of stock.
                    </p>
                    <p style={{ marginBottom: 16, color: '#6b7280' }}>
                        Send an email to the customer?
                    </p>
                    {items.length > 0 && (
                        <div
                            style={{
                                background: "#ffffff",
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                                padding: 16,
                                marginBottom: 16,
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}
                        >
                            <strong style={{ display: "block", marginBottom: 12, fontSize: '14px', color: '#374151' }}>
                                Out of Stock items
                            </strong>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {items.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '8px',
                                            borderRadius: '6px',
                                            border: '1px solid #e5e7eb',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {item.product_image ? (
                                            <div style={{ flexShrink: 0 }}>
                                                <img
                                                    src={item.product_image}
                                                    alt={item.product_title || item.product_sku}
                                                    style={{
                                                        display: 'block',
                                                        width: '80px',
                                                        height: '80px',
                                                        objectFit: 'contain',
                                                        objectPosition: 'center center',
                                                        borderRadius: '4px',
                                                        border: '1px solid #e5e7eb'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        ) : null}
                                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                            <div style={{ fontWeight: 500, fontSize: '13px', color: '#111827', marginBottom: '4px' }}>
                                                {item.product_title || item.product_sku || "Item"}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                                {item.product_sku && (
                                                    <span>SKU: <strong>{item.product_sku}</strong></span>
                                                )}
                                                {item.return_quantity && (
                                                    <span>Qty: <strong>{item.return_quantity}</strong></span>
                                                )}
                                                {item.return_amount && (() => {
                                                    const qty = Number(item.return_quantity || 0);
                                                    const returnAmount = Number(item.return_amount || 0);
                                                    const displayPrice = qty > 1 ? (returnAmount * qty) : returnAmount;
                                                    return <span>Price: <strong>₹{displayPrice.toFixed(2)}</strong></span>;
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="d-flex justify-content-between" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                    <Button color="secondary" onClick={handleSkipEmail} disabled={isSending} style={{ minWidth: '80px' }}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleConfirmSendEmail} disabled={isSending} style={{ minWidth: '120px' }}>
                        Send email
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} size="lg" toggle={onClose} fade={true}>
            <ModalHeader toggle={onClose}>Send out of stock email</ModalHeader>
            <ModalBody>
                <Row>
                    <Col md="6">
                        <FormGroup>
                            <Label>To</Label>
                            <Input
                                type="email"
                                value={to}
                                onChange={(e) => {
                                    setTo(e.target.value);
                                    if (toError) setToError(validateTo(e.target.value));
                                }}
                                onBlur={() => setToError(validateTo())}
                                placeholder="customer@email.com"
                                className={toError ? "is-invalid" : ""}
                            />
                            {toError && <div className="invalid-feedback d-block">{toError}</div>}
                        </FormGroup>
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Label>From</Label>
                            <Input
                                type="email"
                                value={from}
                                onChange={(e) => {
                                    setFrom(e.target.value);
                                    if (fromError) setFromError(validateFrom(e.target.value));
                                }}
                                onBlur={() => setFromError(validateFrom())}
                                placeholder={process.env.REACT_APP_CUSTOMER_SERVICE_EMAIL || "Enter email address"}
                                className={fromError ? "is-invalid" : ""}
                            />
                            {fromError && <div className="invalid-feedback d-block">{fromError}</div>}
                        </FormGroup>
                    </Col>
                </Row>

                <button
                    type="button"
                    className="btn btn-link px-0 mb-2"
                    onClick={() => setShowRecipients(!showRecipients)}
                >
                    Cc and Bcc recipients {showRecipients ? "▲" : "▼"}
                </button>
                {showRecipients && (
                    <div className="mb-3">
                        <FormGroup>
                            <Label className="mb-1">Cc</Label>
                            <Input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Comma separated" />
                        </FormGroup>
                        <FormGroup className="mb-1">
                            <Label className="mb-1">Bcc</Label>
                            <Input type="text" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="Comma separated" />
                        </FormGroup>
                    </div>
                )}

                <FormGroup>
                    <Label>Subject</Label>
                    <Input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={emailData?.order_name ? `Out of Stock Notification - Order #${emailData.order_name.replace(/^#+/, "")}` : "Out of Stock Notification"}
                    />
                </FormGroup>

                <FormGroup className="mb-2">
                    <Label>Custom message</Label>
                    <Input
                        type="textarea"
                        rows="4"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Let the customer know what happened and next steps"
                        style={{ resize: 'vertical' }}
                    />
                </FormGroup>

                <FormGroup className="mt-2 mb-0" style={{ paddingLeft: 28, marginBottom: 12 }}>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <Input
                            type="checkbox"
                            id="includePromo"
                            checked={includePromo}
                            onChange={(e) => setIncludePromo(e.target.checked)}
                        />
                        <Label
                            htmlFor="includePromo"
                            className="mb-0 d-flex align-items-center"
                            style={{ marginTop: 6 }}
                        >
                            Add promo code
                        </Label>
                    </div>
                    {includePromo && (
                        <Input
                            className="mt-2"
                            type="text"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            placeholder="Enter promo code"
                        />
                    )}
                </FormGroup>
                <Label className="mb-2 mt-3">Email preview</Label>
                <div
                    style={{
                        width: "100%",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 12,
                        maxHeight: 360,
                        overflowY: "auto",
                        background: "#fdfdfd",
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)"
                    }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
                <Button color="secondary" onClick={() => setShowEmailForm(false)} disabled={isSending}>
                    Back
                </Button>
                <div>
                    <Button color="secondary" onClick={onClose} disabled={isSending} className="mr-2">
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSend}
                        disabled={
                            isSending ||
                            !to.trim() ||
                            !!toError ||
                            !!fromError ||
                            !from.trim() ||
                            !subject.trim() ||
                            (!hasRefundIds && !hasItems) ||
                            !isValidEmail(to.trim()) ||
                            !isValidEmail(from.trim())
                        }
                    >
                        {isSending && <i className="fa fa-spinner fa-spin mr-2"></i>}
                        Send email
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default OutOfStockEmailModal;
